import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSupabaseClient } from '../supabaseClient';

// The signed-in Supabase user, shared across the app.
//
// After Google sign-in, Supabase sends the visitor back to the site with a
// one-time `?code=` in the URL. Creating the Supabase client exchanges that
// code for a session (and removes it from the URL), so the client has to be
// started on whatever page the visitor lands on, not only on /login.
const AuthContext = createContext({
  user: null,
  loading: false,
  signInWithGoogle: async () => {},
  signOut: async () => {},
  getAccessToken: async () => null
});

// Google always returns visitors to the home page; a page that needs them back
// (e.g. billing) saves its path here before sending them to Google.
const RETURN_KEY = 'codeazy:after-login';
const takeReturnPath = () => {
  try {
    const path = window.sessionStorage.getItem(RETURN_KEY);
    window.sessionStorage.removeItem(RETURN_KEY);
    return path;
  } catch {
    return null;
  }
};

// Only start Supabase for visitors who are returning from Google or already
// have a saved session, so everyone else skips the /api/auth/config request.
const hasAuthState = () => {
  try {
    if (new URLSearchParams(window.location.search).has('code')) return true;
    return Object.keys(window.localStorage).some((key) => key.startsWith('sb-') && key.endsWith('-auth-token'));
  } catch {
    return false;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(hasAuthState);
  // Kept in a ref: navigate can change identity on route changes, and the
  // session should only be set up once
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    if (!hasAuthState()) return undefined;

    let cancelled = false;
    let subscription = null;

    getSupabaseClient()
      .then(async (client) => {
        // Resolves once Supabase has finished starting up, including the code exchange
        const { data } = await client.auth.getSession();
        if (cancelled) return;
        setUser(data.session?.user ?? null);
        // Signed in from a page that asked to come back to it
        const returnPath = data.session ? takeReturnPath() : null;
        if (returnPath) navigateRef.current(returnPath, { replace: true });
        ({ data: { subscription } } = client.auth.onAuthStateChange((_event, session) => {
          setUser(session?.user ?? null);
        }));
      })
      .catch((err) => console.warn('Could not restore the login session:', err.message))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      subscription?.unsubscribe();
    };
  }, []);

  // Sends the visitor to Google; they come back signed in, to `returnTo` if given
  const signInWithGoogle = useCallback(async (returnTo) => {
    try {
      if (returnTo) window.sessionStorage.setItem(RETURN_KEY, returnTo);
    } catch { /* storage unavailable: they land on the home page */ }
    const client = await getSupabaseClient();
    const { error } = await client.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin, queryParams: { prompt: 'select_account' } }
    });
    if (error) throw error;
  }, []);

  // A current access token for API calls (Supabase refreshes it when needed)
  const getAccessToken = useCallback(async () => {
    const client = await getSupabaseClient();
    const { data } = await client.auth.getSession();
    return data.session?.access_token ?? null;
  }, []);

  // Revokes the session everywhere; if Supabase can't be reached, still sign
  // out on this device so the Logout button always works.
  const signOut = useCallback(async () => {
    const client = await getSupabaseClient();
    const { error } = await client.auth.signOut();
    if (error) {
      console.warn('Sign-out request failed, clearing the local session:', error.message);
      await client.auth.signOut({ scope: 'local' });
    }
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, signInWithGoogle, signOut, getAccessToken }),
    [user, loading, signInWithGoogle, signOut, getAccessToken]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
